import { CachedItem } from '@DStvMonoRepo/self-service-core';
import { environment } from 'apps/dstv-via-streaming/src/environments/environment';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { Util } from '../../utils/common.utils';
import { _isEmpty } from '../../utils/function.utils';
import { CacheAreaKey } from '../config/cache-config';
import { AuthService } from '../services/auth';
import { StorageHelper } from '../services/storage';

export interface AssociatedId {
  BU: string;
  CustId: string;
  HomeCountry: string;
  IsMigrated: boolean;
  RegionCode: string;
  id: number;
}

export interface ConnectStore {
  Country: string | null;
  ProfilePicture: string | null;
  access_token: string;
  accessToken: string;
  agreementId: string;
  authenticated: boolean;
  base36Id: string;
  billingCountry: string;
  connectId: string;
  countryCode: string;
  customerNumber: string;
  customer_status: string;
  email: string;
  expires_in: string;
  expires_time: string;
  idToken: string;
  id_token: string;
  mobileNumber: string;
  newCustomer: boolean;
  token_type: string;
  trackingId: string | null;
}

export class AuthStore {
  locale!: string;

  isAuthorized = false;
  isAuthenticated = false;
  isValidating = false;
  isLoading = true;
  refreshTokenPointer!: NodeJS.Timeout;

  customerDetail!: any;

  private akamaiService;
  private readonly cachedConnectModel = new CachedItem('connect-store', true);

  get customerNumber() {
    return this.customerDetail?.customerNumber
  }

  constructor() {
    this.akamaiService = new AuthService();

    makeObservable(this, {
      isAuthorized: observable,
      isAuthenticated: observable,
      isLoading: observable,
      locale: observable,
      customerDetail: observable,
      changeLoadingState: action,
      changeAuthorizedState: action,
      changeAuthenticatedState: action,
      changeCustomerDetail: action,
      changeLocale: action,
      logout: action,
    });
  }

  async initialise(connectStore?: {
    accessToken: string;
    expiresIn: string;
    idToken: string;
    tokenType: string;
    expiresTime: number;
    authenticated?: boolean;
    customerNumber?: string;
  }) {
    if (this.isValidating) {
      return;
    }

    // TODO: check token expiry
    if (this.isAuthorized && !!this.customerNumber) {
      this.changeAuthenticatedState(true);
      this.changeLoadingState(false);
      this.cleanUrl();
      return true;
    }
    this.isValidating = true;

    const token = connectStore || StorageHelper.GetInstance.getFromCache('conf', 'connect', CacheAreaKey.Customer) as ConnectStore;

    if (token && token.accessToken && token.idToken && (!token.authenticated || !!connectStore)) {
      this.cleanUrl();
      return AuthService.authorizeUser({ ...token, forceReAuth: true }).then((res) => {
        this.changeCustomerDetail(res);

        if (res.customerNumber) {
          this.changeAuthenticatedState(true);
        }

        this.changeAuthorizedState(true);
        this.changeLoadingState(false);
        Util.RemoveAllQueryParams();

        const { exp } = AuthService.parseJwt(res.access_token)
        this.scheduleTokenRefresh(exp);
        return true;
      });
    }


    if (token && token.authenticated && token.customerNumber) {
      this.changeCustomerDetail(token);
      const { exp } = AuthService.parseJwt((token as any).access_token)
      this.scheduleTokenRefresh(exp);

      this.changeAuthenticatedState(true);
      this.changeAuthorizedState(true);
      this.changeLoadingState(false);
      this.cleanUrl();
      return true;
    }

    StorageHelper.GetInstance.removeFromStorage('conf', CacheAreaKey.Customer)
    this.changeAuthenticatedState(false);
    this.changeAuthorizedState(false);
    this.changeLoadingState(false);
    return false;
  }

  changeLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  changeAuthorizedState(isAuthorized: boolean) {
    this.isAuthorized = isAuthorized;
  }

  changeAuthenticatedState(isAuthenticated: boolean) {
    this.isAuthenticated = isAuthenticated;
  }

  logout() {
    this.changeAuthorizedState(false);
    this.changeAuthenticatedState(false);
    StorageHelper.GetInstance.removeFromStorage('conf');
    StorageHelper.GetInstance.removeFromStorage('cnt');
    localStorage.removeItem('connect-store');
  }

  changeCustomerDetail(customerDetail: any) {
    if (this.isAuthorized && !this.isAuthenticated && customerDetail.customerNumber) {
      this.changeAuthenticatedState(true);
    }

    StorageHelper.GetInstance.setCache('conf', customerDetail, CacheAreaKey.Customer, 'connect')
    localStorage.setItem('connect-store', JSON.stringify(customerDetail));
    this.customerDetail = customerDetail;
  }

  changeLocale(locale: string) {
    this.locale = locale;
  }

  cleanUrl() {
    const urls = window.location.href.split("#");
    if (urls.length >= 3) {
      window.history.pushState("data", "", `${urls[0]}#${urls[1]}`);
    }
  }

  private scheduleTokenRefresh(expiry: number) {
    if (!environment.USE_REFRESH_TOKEN) {
      return;
    }

    const access = StorageHelper.GetInstance.getFromCache('conf', 'connect', CacheAreaKey.Customer) as ConnectStore;

    if (_isEmpty(access)) {
      return;
    }

    if (this.refreshTokenPointer) {
      clearTimeout(this.refreshTokenPointer);
    }

    const expiresTime = moment
      .unix(expiry)
      .subtract(environment.REFRESH_TOKEN_TOLERANCE, 'minutes');
    const refreshTime = expiresTime.diff(moment(), 'milliseconds')

    if (isNaN(refreshTime)) {
      this.logout();
      return;
    }

    this.refreshTokenPointer = setTimeout(() => {
      this.attemptTokenRefresh().then((res: any) => {
        if (res) {
          this.changeAuthorizedState(true);
        } else {
          // this.logout();
        }
      });
    }, refreshTime);
  }

  private attemptTokenRefresh() {
    if (!environment.USE_REFRESH_TOKEN) {
      return Promise.reject('Refresh Failed');
    }

    const access = StorageHelper.GetInstance.getFromCache('conf', 'connect', CacheAreaKey.Customer) as ConnectStore;

    if (_isEmpty(access)) {
      return Promise.reject('No Refresh Token Found');
    }

    return AuthService.refreshToken(access).then(
      (res: any) => {
        const { expiry } = res;
        StorageHelper.GetInstance.setCache('conf', { ...access, ...res }, CacheAreaKey.Customer, 'connect')
        this.scheduleTokenRefresh(expiry);
        return res;
      },
      (error: any) => {
        console.error('Attempt token refresh: ', error);
        return Promise.reject('Refresh Failed');
      }
    );
  }
}
